import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import { Switch, FormControlLabel, MenuItem } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { webCategoryService } from '../../services/webCategoryService';
import useResponsive from '../../hooks/useResponsive';
import PageHeaderLayout from '../../components/Layout/PageHeaderLayout';

const LocationContentManagement = () => {
  const isMobile = useResponsive('down', 'sm');
  const menuDispatch = useDispatch();
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editingContent, setEditingContent] = useState(null); // SubCategory being edited
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expandedAreaIndex, setExpandedAreaIndex] = useState(null);
  const [openBulkImport, setOpenBulkImport] = useState(false);
  const [bulkJson, setBulkJson] = useState('');
  const [bulkJsonError, setBulkJsonError] = useState('');
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Set active menu
  useEffect(() => {
    menuDispatch({
      type: 'menuSelect',
      payload: 'web-location-content'
    });
    fetchCategories();
  }, []);

  const { control, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      // Location Identity
      name: '',
      slug: '',
      subCatDescription: '',
      // Meta and Page Info
      breadCrumb: '',
      image: '',
      metaTitle: '',
      metaDescription: '',
      heading: '',
      subHeading: '',
      url: '',
      title: '',
      description: '',
      description2: '',
      
      // Intro Section
      introTitle: '',
      introText: '',
      
      // Why Choose Us / Benefits
      whyChooseUsTitle: '',
      whyChooseUsText: '',
      benefits: [''],
      
      // Services Section
      servicesTitle: '',
      servicesText: '',
      services: [''],
      
      // Tech Section
      techTitle: '',
      techText: '',
      techFeatures: [''],
      
      // Local Expertise
      localExpertiseTitle: '',
      localExpertiseText: '',
      closing: '',
      
      // What We Cover
      whatweCover: '',
      wwcSubHeading: '',
      servicesCovered: [''],
      serviceFeatures: '',
      
      // Installation Services
      installationServices: {
        heading: '',
        description: '',
        subDescription: '',
        includes: [''],
        comment: ''
      },
      
      // Service Areas with Location Data
      serviceAreas: {
        heading: '',
        subDescription: '',
        areas: [],
        comment: ''
      },
      
      // Why Choose Us (duplicate structure for compatibility)
      whyChooseUs: {
        heading: '',
        areas: ['']
      },
      
      // Supported Brands
      supportedBrands: [''],

      // Booking CTA
      bookingCTA: {
        heading: '',
        description: '',
        comment: ''
      },

      // FAQs
      faqsHeading: 'Frequently Asked Questions',
      faqs: [{ question: '', answer: '' }],

      // Service Areas structured list
      serviceAreasAreas: [],

      // Sitemap
      includeSitemap: false,
      sitemapLastmod: new Date().toISOString().split('T')[0],
      sitemapPriority: '0.80'
    }
  });

  // Auto-generate slug from name
  const watchedName = watch('name');
  const watchedSlug = watch('slug');
  useEffect(() => {
    if (watchedName && !editingContent) {
      const slug = watchedName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-');
      setValue('slug', slug);
    }
  }, [watchedName]);

  // Auto-generate URL path from category slug + form slug
  useEffect(() => {
    if (!selectedCategory || !watchedSlug) return;
    const catSlug = selectedCategory.slug ||
      selectedCategory.title.toLowerCase().replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-');
    setValue('url', `${catSlug}/${watchedSlug}`);
  }, [watchedSlug, selectedCategory]);

  // Field arrays for dynamic lists
  const { fields: benefitFields, append: appendBenefit, remove: removeBenefit } = 
    useFieldArray({ control, name: 'benefits' });
  
  const { fields: serviceFields, append: appendService, remove: removeService } = 
    useFieldArray({ control, name: 'services' });
  
  const { fields: techFields, append: appendTech, remove: removeTech } = 
    useFieldArray({ control, name: 'techFeatures' });
  
  const { fields: serviceCoveredFields, append: appendServiceCovered, remove: removeServiceCovered } = 
    useFieldArray({ control, name: 'servicesCovered' });
  
  const { fields: includesFields, append: appendIncludes, remove: removeIncludes } = 
    useFieldArray({ control, name: 'installationServices.includes' });
  
  const { fields: whyChooseUsFields, append: appendWhyChooseUs, remove: removeWhyChooseUs } = 
    useFieldArray({ control, name: 'whyChooseUs.areas' });
  
  const { fields: faqFields, append: appendFaq, remove: removeFaq } =
    useFieldArray({ control, name: 'faqs' });

  const { fields: supportedBrandFields, append: appendSupportedBrand, remove: removeSupportedBrand } =
    useFieldArray({ control, name: 'supportedBrands' });

  const { fields: serviceAreaFields, append: appendServiceArea, remove: removeServiceArea } =
    useFieldArray({ control, name: 'serviceAreasAreas' });

  // Helpers to convert between data.js area format and flat form format
  const EMPTY_AREA_FIELDS = {
    url: '', metaTitle: '', metaDescription: '', image: '',
    contentHeading: '', contentSubheading: '',
    introTitle: '', introText: '',
    whyChooseUsTitle: '', whyChooseUsText: '',
    benefitsText: '',
    servicesTitle: '', servicesText: '', servicesListText: '',
    techTitle: '', techText: '', techFeaturesText: '',
    localExpertiseTitle: '', localExpertiseText: '', closing: ''
  };

  const areasToFormAreas = (areas) =>
    (areas || []).map(area => {
      if (typeof area === 'string') {
        return { type: 'simple', name: area, ...EMPTY_AREA_FIELDS };
      }
      const content = area.data?.[0]?.content || {};
      const meta = area.data?.[0]?.meta || {};
      return {
        type: 'detailed',
        name: area.name || '',
        url: area.url || '',
        metaTitle: meta.title || '',
        metaDescription: meta.description || '',
        image: content.image || '',
        contentHeading: content.heading || '',
        contentSubheading: content.subheading || '',
        introTitle: content.introTitle || '',
        introText: content.introText || '',
        whyChooseUsTitle: content.whyChooseUsTitle || '',
        whyChooseUsText: content.whyChooseUsText || '',
        benefitsText: (content.benefits || []).join('\n'),
        servicesTitle: content.servicesTitle || '',
        servicesText: content.servicesText || '',
        servicesListText: (content.services || []).join('\n'),
        techTitle: content.techTitle || '',
        techText: content.techText || '',
        techFeaturesText: (content.techFeatures || []).join('\n'),
        localExpertiseTitle: content.localExpertiseTitle || '',
        localExpertiseText: content.localExpertiseText || '',
        closing: content.closing || ''
      };
    });

  const formAreasToData = (areas) =>
    (areas || []).map(area => {
      if (area.type === 'simple') return area.name;
      return {
        name: area.name,
        url: area.url,
        data: [{
          meta: { title: area.metaTitle, description: area.metaDescription },
          content: {
            image: area.image,
            heading: area.contentHeading,
            subheading: area.contentSubheading,
            introTitle: area.introTitle,
            introText: area.introText,
            whyChooseUsTitle: area.whyChooseUsTitle,
            whyChooseUsText: area.whyChooseUsText,
            benefits: area.benefitsText ? area.benefitsText.split('\n').filter(b => b.trim()) : [],
            servicesTitle: area.servicesTitle,
            servicesText: area.servicesText,
            services: area.servicesListText ? area.servicesListText.split('\n').filter(s => s.trim()) : [],
            techTitle: area.techTitle,
            techText: area.techText,
            techFeatures: area.techFeaturesText ? area.techFeaturesText.split('\n').filter(t => t.trim()) : [],
            localExpertiseTitle: area.localExpertiseTitle,
            localExpertiseText: area.localExpertiseText,
            closing: area.closing
          }
        }]
      };
    });

  const fetchCategories = async () => {
    try {
      const response = await webCategoryService.getParentCategoriesForDropdown({ limit: 100, isActive: true });
      if (response.success) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSubCategories = async (categoryId) => {
    try {
      const response = await webCategoryService.getSubCategoriesByCategoryId(
        categoryId,
        { limit: 100 }
      );
      if (response.success) {
        setSubCategories(response.data);
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSubCategories([]);
    if (category) {
      fetchSubCategories(category.id);
    }
  };

  const handleAddLocation = () => {
    reset();
    setEditingContent(null);
    setOpenDialog(true);
  };

  const handleEditLocation = (subCat) => {
    setEditingContent(subCat);
    const content = subCat.subData?.[0] || {};
    const formData = {
      name: subCat.name || '',
      slug: subCat.slug || '',
      subCatDescription: subCat.description || '',
      ...content,
      subHeading: content.subheading || content.subHeading || '',
      faqs: Array.isArray(content.faqs)
        ? content.faqs
        : (content.faqs?.areas || [{ question: '', answer: '' }]),
      benefits: content.benefits || [''],
      services: content.services || [''],
      techFeatures: content.techFeatures || [''],
      servicesCovered: content.servicesCovered || [''],
      installationServices: {
        heading: content.installationServices?.heading || '',
        description: content.installationServices?.description || '',
        subDescription: content.installationServices?.subDescription || '',
        includes: content.installationServices?.includes || [''],
        comment: content.installationServices?.comment || ''
      },
      serviceAreas: {
        heading: content.serviceAreas?.heading || '',
        subDescription: content.serviceAreas?.subDescription || '',
        areas: [],
        comment: content.serviceAreas?.comment || ''
      },
      serviceAreasAreas: areasToFormAreas(content.serviceAreas?.areas),
      supportedBrands: content.supportedBrands?.length ? content.supportedBrands : [''],
      whyChooseUs: {
        heading: content.whyChooseUs?.heading || '',
        areas: content.whyChooseUs?.areas || ['']
      },
      bookingCTA: {
        heading: content.bookingCTA?.heading || '',
        description: content.bookingCTA?.description || '',
        comment: content.bookingCTA?.comment || ''
      },
      includeSitemap: content.includeSitemap || false,
      sitemapLastmod: content.sitemapLastmod
        ? new Date(content.sitemapLastmod).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      sitemapPriority: content.sitemapPriority || '0.80'
    };
    reset(formData);
    setOpenDialog(true);
  };

  const handleDeleteLocation = async (subCatId) => {
    if (!window.confirm('Are you sure you want to delete this location? This action cannot be undone.')) {
      return;
    }
    try {
      setLoading(true);
      await webCategoryService.deleteCategory(subCatId);
      await fetchSubCategories(selectedCategory.id);
      showSnackbar('Location deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting:', error);
      showSnackbar(error.response?.data?.message || 'Error deleting location', 'error');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      
      // Structure data to match subData format from data.js
      const subDataEntry = {
        breadCrumb: data.breadCrumb,
        image: data.image,
        metaTitle: data.metaTitle,
        metaDescription: data.metaDescription,
        heading: data.heading,
        subHeading: data.subHeading,
        url: data.url,
        title: data.title,
        description: data.description || '',
        description2: data.description2,
        
        whatweCover: data.whatweCover,
        wwcSubHeading: data.wwcSubHeading,
        servicesCovered: (data.servicesCovered || []).filter(s => s && s.trim()),
        serviceFeatures: data.serviceFeatures,
        
        installationServices: {
          heading: data.installationServices?.heading || '',
          description: data.installationServices?.description || '',
          subDescription: data.installationServices?.subDescription || '',
          includes: (data.installationServices?.includes || []).filter(i => i && i.trim()),
          comment: data.installationServices?.comment || ''
        },
        
        serviceAreas: {
          heading: data.serviceAreas?.heading || '',
          subDescription: data.serviceAreas?.subDescription || '',
          areas: formAreasToData(data.serviceAreasAreas),
          comment: data.serviceAreas?.comment || ''
        },
        supportedBrands: (data.supportedBrands || []).filter(b => b && b.trim()),
        
        whyChooseUs: {
          heading: data.whyChooseUs?.heading || '',
          areas: (data.whyChooseUs?.areas || []).filter(a => a && a.trim())
        },
        
        bookingCTA: {
          heading: data.bookingCTA?.heading || '',
          description: data.bookingCTA?.description || '',
          comment: data.bookingCTA?.comment || ''
        },
        
        faqs: {
          heading: data.faqsHeading || 'Frequently Asked Questions',
          areas: Array.isArray(data.faqs)
            ? data.faqs.filter(f => f.question && f.answer)
            : (data.faqs?.areas || []).filter(f => f.question && f.answer)
        },
        
        introTitle: data.introTitle,
        introText: data.introText,
        whyChooseUsTitle: data.whyChooseUsTitle,
        whyChooseUsText: data.whyChooseUsText,
        benefits: (data.benefits || []).filter(b => b && b.trim()),
        servicesTitle: data.servicesTitle,
        servicesText: data.servicesText,
        services: (data.services || []).filter(s => s && s.trim()),
        techTitle: data.techTitle,
        techText: data.techText,
        techFeatures: (data.techFeatures || []).filter(t => t && t.trim()),
        localExpertiseTitle: data.localExpertiseTitle,
        localExpertiseText: data.localExpertiseText,
        closing: data.closing,

        // Sitemap
        includeSitemap: data.includeSitemap || false,
        sitemapLastmod: data.includeSitemap ? data.sitemapLastmod : undefined,
        sitemapPriority: data.sitemapPriority || '0.80'
      };
      
      console.log('Saving subData:', {
        editingId: editingContent?._id,
        subData: subDataEntry,
        isEditing: !!editingContent
      });
      
      // Check if we're editing or creating
      if (editingContent) {
        await webCategoryService.updateCategory(editingContent._id, {
          name: data.name,
          slug: data.slug,
          description: data.subCatDescription || '',
          subData: [subDataEntry]
        });
        await fetchSubCategories(selectedCategory.id);
      } else {
        // Create new SubCategory record
        await webCategoryService.addSubDataToSubCategory(selectedCategory.id, {
          name: data.name,
          slug: data.slug,
          description: data.subCatDescription || '',
          subData: [subDataEntry]
        });
        await fetchSubCategories(selectedCategory.id);
      }
      
      showSnackbar(
        editingContent ? 'Location updated successfully' : 'Location added successfully',
        'success'
      );
      setOpenDialog(false);
      setEditingContent(null);
      setLoading(false);
    } catch (error) {
      console.error('Error saving:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error saving location content';
      showSnackbar(errorMessage, 'error');
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleBulkImport = async () => {
    setBulkJsonError('');
    let items;
    try {
      // Try strict JSON first, then fall back to JS object notation
      try {
        items = JSON.parse(bulkJson);
      } catch {
        // eslint-disable-next-line no-new-func
        items = Function('"use strict"; return (' + bulkJson + ')')();
      }
      // Accept both: array of subCategories OR a single category object with subCategories
      if (!Array.isArray(items)) {
        if (items && Array.isArray(items.subCategories)) {
          items = items.subCategories;
        } else {
          throw new Error('Must be an array, or a category object with a subCategories array');
        }
      }
    } catch (e) {
      setBulkJsonError('Parse error: ' + e.message);
      return;
    }
    setBulkImporting(true);
    setBulkProgress({ done: 0, total: items.length });
    let successCount = 0;
    let failCount = 0;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      try {
        await webCategoryService.addSubDataToSubCategory(selectedCategory.id, {
          name: item.name || '',
          slug: item.slug || '',
          description: item.description || '',
          subData: item.subData || []
        });
        successCount++;
      } catch (err) {
        console.error('Bulk import error for item:', item.name, err);
        failCount++;
      }
      setBulkProgress({ done: i + 1, total: items.length });
    }
    await fetchSubCategories(selectedCategory.id);
    setBulkImporting(false);
    setOpenBulkImport(false);
    setBulkJson('');
    showSnackbar(
      `Bulk import done: ${successCount} created, ${failCount} failed`,
      failCount > 0 ? 'warning' : 'success'
    );
  };

  return (
    <Box>
      <PageHeaderLayout headerTitle="Location-Specific Content Management">
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => { setBulkJson(''); setBulkJsonError(''); setOpenBulkImport(true); }}
            disabled={!selectedCategory}
            sx={{ minWidth: { xs: 'auto', sm: 160 } }}
          >
            {isMobile ? 'Import' : 'Bulk Import'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddLocation}
            disabled={!selectedCategory}
            sx={{ minWidth: { xs: 'auto', sm: 180 } }}
          >
            {isMobile ? 'Add' : 'Add Location'}
          </Button>
        </Box>
      </PageHeaderLayout>

      <Paper sx={{ p: 3, mt: 2 }}>
        {/* Category Selection */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={categories}
              getOptionLabel={(option) => option.title}
              value={selectedCategory}
              onChange={(_, newValue) => handleCategoryChange(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Select Category" required />
              )}
            />
          </Grid>
        </Grid>

        {selectedCategory && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Locations for: {selectedCategory.title}
            </Typography>

            {subCategories.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LocationIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  No locations added yet. Click "Add Location" to create the first one.
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Slug</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subCategories.map((subCat, index) => (
                      <TableRow key={subCat._id} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {subCat.name || 'Untitled'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={subCat.slug || 'No Slug'}
                            size="small"
                            variant="outlined"
                            sx={{ maxWidth: 200 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                            {subCat.description || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Button
                              startIcon={<EditIcon />}
                              onClick={() => handleEditLocation(subCat)}
                              variant="contained"
                              size="small"
                              color="primary"
                            >
                              Edit
                            </Button>
                            <IconButton
                              color="error"
                              size="small"
                              title="Delete"
                              onClick={() => handleDeleteLocation(subCat._id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>

      {/* Location Content Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setEditingContent(null);
        }}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            height: isMobile ? '100%' : 'auto',
            width:'90%'
          }
        }}
      >
        <DialogTitle sx={{ 
          position: 'sticky', 
          top: 0, 
          bgcolor: 'background.paper', 
          zIndex: 1,
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          {editingContent ? 'Edit Location Content' : 'Add Location Content'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ pt: 2 }}>
            {/* Location Info */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Basic Information
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: 'Location name is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Sub Category Name"
                      fullWidth
                      required
                      error={!!errors.name}
                      helperText={errors.name?.message || 'e.g., Women Salon & Spa'}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="slug"
                  control={control}
                  rules={{ required: 'Slug is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Slug"
                      fullWidth
                      required
                      error={!!errors.slug}
                      helperText={errors.slug?.message || 'Auto-generated from name'}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="subCatDescription"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description"
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Brief description of this location page"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="breadCrumb"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Breadcrumb"
                      fullWidth
                      placeholder="e.g., AC Repair Services"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="url"
                  control={control}
                  rules={{ required: 'URL is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="URL Path"
                      fullWidth
                      required
                      error={!!errors.url}
                      helperText={errors.url?.message || 'e.g., /category/ac-repair-services/window-ac'}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="image"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Image URL"
                      fullWidth
                      placeholder="https://cudel.s3.amazonaws.com/..."
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Meta Tags */}
            <Typography variant="h6" gutterBottom>
              SEO Meta Tags
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <Controller
                  name="metaTitle"
                  control={control}
                  rules={{
                    required: 'Meta title is required',
                    maxLength: { value: 60, message: 'Max 60 characters' }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Meta Title"
                      fullWidth
                      required
                      error={!!errors.metaTitle}
                      helperText={`${field.value?.length || 0}/60 characters`}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="metaDescription"
                  control={control}
                  rules={{
                    required: 'Meta description is required',
                    maxLength: { value: 160, message: 'Max 160 characters' }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Meta Description"
                      fullWidth
                      multiline
                      rows={2}
                      required
                      error={!!errors.metaDescription}
                      helperText={`${field.value?.length || 0}/160 characters`}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Page Content */}
            <Typography variant="h6" gutterBottom>
              Page Content
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="heading"
                  control={control}
                  rules={{ required: 'Heading is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Main Heading"
                      fullWidth
                      required
                      error={!!errors.heading}
                      helperText={errors.heading?.message}
                      placeholder="Window AC Repair Service in Noida"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="subHeading"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Sub Heading"
                      fullWidth
                      placeholder="Expert Professional Window AC Repair..."
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Page Title"
                      fullWidth
                      placeholder="Doorstep Window AC Repair Services by Experts"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description 1"
                      fullWidth
                      multiline
                      rows={4}
                      placeholder="Main description text..."
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="description2"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description 2"
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Additional description text..."
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="introTitle"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Intro Title" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="introText"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Intro Text" fullWidth multiline rows={3} />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="closing"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Closing Text"
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Don't let AC issues eat into your comfort..."
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* What We Cover Section */}
            <Typography variant="h6" gutterBottom>
              What We Cover Section
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <Controller
                  name="whatweCover"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="What We Offer/Cover" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="wwcSubHeading"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="What We Cover Sub Heading" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">Services Covered</Typography>
                  <Button size="small" onClick={() => appendServiceCovered('')}>Add Service</Button>
                </Box>
                {serviceCoveredFields.map((field, index) => (
                  <Box key={field.id} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Controller
                      name={`servicesCovered.${index}`}
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} fullWidth size="small" placeholder={`Service ${index + 1}`} />
                      )}
                    />
                    <IconButton onClick={() => removeServiceCovered(index)} color="error" size="small">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="serviceFeatures"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Service Features"
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="We use genuine spare parts, advanced tools..."
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Benefits Section */}
            <Typography variant="h6" gutterBottom>
              Benefits / Why Choose Us Section
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <Controller
                  name="whyChooseUsTitle"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Section Title" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="whyChooseUsText"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Section Description" fullWidth multiline rows={2} />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">Benefits List</Typography>
                  <Button size="small" onClick={() => appendBenefit('')}>Add Benefit</Button>
                </Box>
                {benefitFields.map((field, index) => (
                  <Box key={field.id} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Controller
                      name={`benefits.${index}`}
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} fullWidth multiline size="small" placeholder={`Benefit ${index + 1}`} />
                      )}
                    />
                    <IconButton onClick={() => removeBenefit(index)} color="error" size="small">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Services Section */}
            <Typography variant="h6" gutterBottom>
              Services Section
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <Controller
                  name="servicesTitle"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Services Title" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="servicesText"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Services Description" fullWidth multiline rows={2} />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">Services List</Typography>
                  <Button size="small" onClick={() => appendService('')}>Add Service</Button>
                </Box>
                {serviceFields.map((field, index) => (
                  <Box key={field.id} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Controller
                      name={`services.${index}`}
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} fullWidth size="small" placeholder={`Service ${index + 1}`} />
                      )}
                    />
                    <IconButton onClick={() => removeService(index)} color="error" size="small">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Tech Section */}
            <Typography variant="h6" gutterBottom>
              Technical Features Section
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <Controller
                  name="techTitle"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Tech Section Title" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="techText"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Tech Section Description" fullWidth multiline rows={2} />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">Technical Features</Typography>
                  <Button size="small" onClick={() => appendTech('')}>Add Feature</Button>
                </Box>
                {techFields.map((field, index) => (
                  <Box key={field.id} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Controller
                      name={`techFeatures.${index}`}
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} fullWidth multiline size="small" placeholder={`Feature ${index + 1}`} />
                      )}
                    />
                    <IconButton onClick={() => removeTech(index)} color="error" size="small">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Local Expertise Section */}
            <Typography variant="h6" gutterBottom>
              Local Expertise Section
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <Controller
                  name="localExpertiseTitle"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Local Expertise Title" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="localExpertiseText"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Local Expertise Text" fullWidth multiline rows={3} />
                  )}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Installation Services Section */}
            <Typography variant="h6" gutterBottom>
              Installation Services Section
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <Controller
                  name="installationServices.heading"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Installation Heading" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="installationServices.description"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Installation Description" fullWidth multiline rows={2} />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="installationServices.subDescription"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Installation Sub Description" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">Installation Includes</Typography>
                  <Button size="small" onClick={() => appendIncludes('')}>Add Item</Button>
                </Box>
                {includesFields.map((field, index) => (
                  <Box key={field.id} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Controller
                      name={`installationServices.includes.${index}`}
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} fullWidth size="small" placeholder={`Include ${index + 1}`} />
                      )}
                    />
                    <IconButton onClick={() => removeIncludes(index)} color="error" size="small">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="installationServices.comment"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Installation Comment" fullWidth />
                  )}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Service Areas Section */}
            <Typography variant="h6" gutterBottom>
              Service Areas Section
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <Controller
                  name="serviceAreas.heading"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Service Areas Heading" fullWidth placeholder="Looking for an air conditioner repair near you?" />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="serviceAreas.subDescription"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Service Areas Sub Description" fullWidth placeholder="We operate across Delhi NCR, including:" />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">Areas</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        appendServiceArea({ type: 'simple', name: '', ...EMPTY_AREA_FIELDS });
                        setExpandedAreaIndex(serviceAreaFields.length);
                      }}
                    >
                      + Simple
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      onClick={() => {
                        appendServiceArea({ type: 'detailed', name: '', ...EMPTY_AREA_FIELDS });
                        setExpandedAreaIndex(serviceAreaFields.length);
                      }}
                    >
                      + Detailed
                    </Button>
                  </Box>
                </Box>

                {serviceAreaFields.map((field, index) => {
                  const isExpanded = expandedAreaIndex === index;
                  const areaType = watch(`serviceAreasAreas.${index}.type`);
                  return (
                    <Paper key={field.id} variant="outlined" sx={{ mb: 1, overflow: 'hidden' }}>
                      {/* Area header row */}
                      <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, bgcolor: 'grey.50', gap: 1 }}>
                        <Chip
                          label={areaType === 'detailed' ? 'Detailed' : 'Simple'}
                          size="small"
                          color={areaType === 'detailed' ? 'primary' : 'default'}
                          sx={{ minWidth: 70, flexShrink: 0 }}
                        />
                        <Controller
                          name={`serviceAreasAreas.${index}.name`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              size="small"
                              placeholder={areaType === 'detailed' ? 'City name (e.g. Noida)' : 'Area name (e.g. Indirapuram)'}
                              sx={{ flex: 1 }}
                            />
                          )}
                        />
                        {areaType === 'detailed' && (
                          <IconButton
                            size="small"
                            onClick={() => setExpandedAreaIndex(isExpanded ? null : index)}
                            title={isExpanded ? 'Collapse' : 'Edit content'}
                          >
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            removeServiceArea(index);
                            if (expandedAreaIndex === index) setExpandedAreaIndex(null);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>

                      {/* Detailed area expanded content */}
                      {areaType === 'detailed' && isExpanded && (
                        <Box sx={{ p: 2 }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Controller
                                name={`serviceAreasAreas.${index}.url`}
                                control={control}
                                render={({ field }) => (
                                  <TextField {...field} label="URL Slug" fullWidth size="small" placeholder="noida" />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Controller
                                name={`serviceAreasAreas.${index}.image`}
                                control={control}
                                render={({ field }) => (
                                  <TextField {...field} label="Image URL" fullWidth size="small" />
                                )}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>SEO Meta</Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Controller
                                name={`serviceAreasAreas.${index}.metaTitle`}
                                control={control}
                                render={({ field }) => (
                                  <TextField {...field} label="Meta Title" fullWidth size="small" />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Controller
                                name={`serviceAreasAreas.${index}.metaDescription`}
                                control={control}
                                render={({ field }) => (
                                  <TextField {...field} label="Meta Description" fullWidth size="small" multiline rows={2} />
                                )}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Page Content</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Controller
                                name={`serviceAreasAreas.${index}.contentHeading`}
                                control={control}
                                render={({ field }) => (
                                  <TextField {...field} label="Heading" fullWidth size="small" />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Controller
                                name={`serviceAreasAreas.${index}.contentSubheading`}
                                control={control}
                                render={({ field }) => (
                                  <TextField {...field} label="Subheading" fullWidth size="small" />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Controller
                                name={`serviceAreasAreas.${index}.introTitle`}
                                control={control}
                                render={({ field }) => (
                                  <TextField {...field} label="Intro Title" fullWidth size="small" />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Controller
                                name={`serviceAreasAreas.${index}.introText`}
                                control={control}
                                render={({ field }) => (
                                  <TextField {...field} label="Intro Text" fullWidth size="small" multiline rows={3} />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Controller
                                name={`serviceAreasAreas.${index}.whyChooseUsTitle`}
                                control={control}
                                render={({ field }) => (
                                  <TextField {...field} label="Why Choose Us Title" fullWidth size="small" />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Controller
                                name={`serviceAreasAreas.${index}.whyChooseUsText`}
                                control={control}
                                render={({ field }) => (
                                  <TextField {...field} label="Why Choose Us Text" fullWidth size="small" multiline rows={2} />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Controller
                                name={`serviceAreasAreas.${index}.benefitsText`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    label="Benefits (one per line)"
                                    fullWidth
                                    size="small"
                                    multiline
                                    rows={4}
                                    helperText="Each benefit on a new line"
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Controller
                                name={`serviceAreasAreas.${index}.servicesTitle`}
                                control={control}
                                render={({ field }) => (
                                  <TextField {...field} label="Services Title" fullWidth size="small" />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Controller
                                name={`serviceAreasAreas.${index}.servicesText`}
                                control={control}
                                render={({ field }) => (
                                  <TextField {...field} label="Services Text" fullWidth size="small" multiline rows={2} />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Controller
                                name={`serviceAreasAreas.${index}.servicesListText`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    label="Services List (one per line)"
                                    fullWidth
                                    size="small"
                                    multiline
                                    rows={3}
                                    helperText="Each service on a new line"
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Controller
                                name={`serviceAreasAreas.${index}.techTitle`}
                                control={control}
                                render={({ field }) => (
                                  <TextField {...field} label="Tech Title" fullWidth size="small" />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Controller
                                name={`serviceAreasAreas.${index}.techText`}
                                control={control}
                                render={({ field }) => (
                                  <TextField {...field} label="Tech Text" fullWidth size="small" multiline rows={2} />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Controller
                                name={`serviceAreasAreas.${index}.techFeaturesText`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    label="Tech Features (one per line)"
                                    fullWidth
                                    size="small"
                                    multiline
                                    rows={3}
                                    helperText="Each feature on a new line"
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Controller
                                name={`serviceAreasAreas.${index}.localExpertiseTitle`}
                                control={control}
                                render={({ field }) => (
                                  <TextField {...field} label="Local Expertise Title" fullWidth size="small" />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Controller
                                name={`serviceAreasAreas.${index}.localExpertiseText`}
                                control={control}
                                render={({ field }) => (
                                  <TextField {...field} label="Local Expertise Text" fullWidth size="small" multiline rows={2} />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Controller
                                name={`serviceAreasAreas.${index}.closing`}
                                control={control}
                                render={({ field }) => (
                                  <TextField {...field} label="Closing Statement" fullWidth size="small" multiline rows={2} />
                                )}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                    </Paper>
                  );
                })}
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="serviceAreas.comment"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Service Areas Comment" fullWidth />
                  )}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Why Choose Us Section */}
            <Typography variant="h6" gutterBottom>
              Why Choose Us Section
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <Controller
                  name="whyChooseUs.heading"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Why Choose Us Heading" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">Why Choose Us Points</Typography>
                  <Button size="small" onClick={() => appendWhyChooseUs('')}>Add Point</Button>
                </Box>
                {whyChooseUsFields.map((field, index) => (
                  <Box key={field.id} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Controller
                      name={`whyChooseUs.areas.${index}`}
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} fullWidth multiline size="small" placeholder={`Point ${index + 1}`} />
                      )}
                    />
                    <IconButton onClick={() => removeWhyChooseUs(index)} color="error" size="small">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Booking CTA Section */}
            <Typography variant="h6" gutterBottom>
              Booking CTA Section
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <Controller
                  name="bookingCTA.heading"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="CTA Heading" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="bookingCTA.description"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="CTA Description" fullWidth multiline rows={2} />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="bookingCTA.comment"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="CTA Comment" fullWidth />
                  )}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Supported Brands Section */}
            <Typography variant="h6" gutterBottom>
              Supported Brands
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">Brands List</Typography>
                  <Button size="small" onClick={() => appendSupportedBrand('')}>Add Brand</Button>
                </Box>
                {supportedBrandFields.map((field, index) => (
                  <Box key={field.id} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Controller
                      name={`supportedBrands.${index}`}
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} fullWidth size="small" placeholder={`Brand ${index + 1} (e.g. LG)`} />
                      )}
                    />
                    <IconButton onClick={() => removeSupportedBrand(index)} color="error" size="small">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* FAQs Section */}
            <Typography variant="h6" gutterBottom>
              FAQs
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <Controller
                  name="faqsHeading"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="FAQs Heading" fullWidth placeholder="Frequently Asked Questions" />
                  )}
                />
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2">Questions & Answers</Typography>
              <Button size="small" onClick={() => appendFaq({ question: '', answer: '' })}>
                Add FAQ
              </Button>
            </Box>
            {faqFields.map((field, index) => (
              <Paper key={field.id} sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Controller
                      name={`faqs.${index}.question`}
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Question" fullWidth size="small" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name={`faqs.${index}.answer`}
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Answer" fullWidth multiline rows={2} size="small" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button onClick={() => removeFaq(index)} color="error" size="small" startIcon={<DeleteIcon />}>
                      Remove FAQ
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            ))}

            <Divider sx={{ my: 3 }} />

            {/* Sitemap Section */}
            <Typography variant="h6" gutterBottom>
              Sitemap Settings
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <Controller
                  name="includeSitemap"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Include this URL in Sitemap"
                    />
                  )}
                />
              </Grid>
              {watch('includeSitemap') && (
                <>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="sitemapLastmod"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Last Modified Date"
                          type="date"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          helperText="Date shown in sitemap lastmod tag"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="sitemapPriority"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          label="Priority"
                          fullWidth
                          helperText="Sitemap URL priority (default 0.80)"
                        >
                          {['1.00', '0.90', '0.80', '0.70', '0.60', '0.50'].map((p) => (
                            <MenuItem key={p} value={p}>{p}</MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </Grid>
                </>
              )}
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Form Actions */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={() => setOpenDialog(false)} variant="outlined" disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? <CircularProgress size={24} /> : 'Save Location Content'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={openBulkImport} onClose={() => !bulkImporting && setOpenBulkImport(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Bulk Import Locations
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Paste the <strong>subCategories array</strong> or the full category object for <strong>{selectedCategory?.title}</strong>.
            Accepts both JavaScript object notation and JSON.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={16}
            value={bulkJson}
            onChange={(e) => { setBulkJson(e.target.value); setBulkJsonError(''); }}
            placeholder={`[\n  {\n    "name": "Window AC Repair Services",\n    "slug": "window-ac-repair-services",\n    "description": "...",\n    "subData": [{ ... }]\n  }\n]`}
            error={!!bulkJsonError}
            helperText={bulkJsonError}
            disabled={bulkImporting}
            sx={{ mt: 1, fontFamily: 'monospace', '& textarea': { fontFamily: 'monospace', fontSize: 13 } }}
          />
          {bulkImporting && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Importing {bulkProgress.done} / {bulkProgress.total}…
              </Typography>
              <LinearProgress
                variant="determinate"
                value={bulkProgress.total ? (bulkProgress.done / bulkProgress.total) * 100 : 0}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenBulkImport(false)} disabled={bulkImporting}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={bulkImporting ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
            onClick={handleBulkImport}
            disabled={!bulkJson.trim() || bulkImporting}
          >
            {bulkImporting ? 'Importing…' : 'Import All'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LocationContentManagement;
